import { Order } from "../pages/panel/types/order";
import { getToBeDeliveredFieldLabel, normalizeCustomerRole } from "./dateFormat";
import {
  buildCsvFilename,
  downloadCsv,
  formatCsvDate,
  formatCsvDateTime,
} from "./csvExport";

export type OrdersCsvOptions = {
  /** Filename prefix, e.g. orders | invoices | orders_delivery */
  prefix: string;
  fromDate?: string;
  toDate?: string;
  /**
   * Column title for toBeDelivered.
   * If omitted, derived from customerRoles.
   */
  deliveryColumnName?: string;
  /** Roles used for this export (drives default delivery column name). */
  customerRoles?: string[];
};

function resolveDeliveryColumnName(opts: OrdersCsvOptions): string {
  if (opts.deliveryColumnName) return opts.deliveryColumnName;
  const roles = (opts.customerRoles || []).map(normalizeCustomerRole).filter(Boolean);
  if (roles.length === 1 && roles[0] === "dealer") return "Company Delivery Date";
  if (roles.length > 0 && roles.every((r) => r === "interior" || r === "end-user" || r === "enduser")) {
    return "Fixing Date";
  }
  if (roles.includes("dealer") && !roles.some((r) => r === "interior" || r === "end-user")) {
    return "Company Delivery Date";
  }
  if (!roles.includes("dealer") && roles.some((r) => r === "interior" || r === "end-user")) {
    return "Fixing Date";
  }
  return "Company Delivery / Fixing Date";
}

/** Build and download a filtered orders/invoices CSV. */
export function exportOrdersToCsv(orders: Order[], opts: OrdersCsvOptions): void {
  const deliveryCol = resolveDeliveryColumnName(opts);

  const headers = [
    "Order ID",
    "Customer",
    "Customer ID",
    "Customer Role",
    "Status",
    "Created By",
    "Created At",
    "Updated At",
    "Subtotal",
    "GST",
    "Discount",
    "Fixing",
    "Transport",
    "Total",
    "Advance 1",
    "Advance 2",
    "Balance",
    "Delivery Date",
    deliveryCol,
    "Quotation Date",
    "Quotation Time",
    "Invoice Generated",
    "Invoice ID",
    "Invoice Date",
    "Total Area",
    "Notes",
    "Mobile",
  ];

  const rows = orders.map((order) => [
    order.id,
    order.customerName,
    order.customerId,
    order.customerRole ?? "",
    order.status,
    order.createdBy,
    formatCsvDateTime(order.createdAt),
    formatCsvDateTime(order.updatedAt),
    order.price,
    order.gst,
    order.discount ?? "",
    order.fixing ?? "",
    order.transport ?? "",
    order.total,
    order.advance ?? "0",
    order.advance2 ?? "0",
    order.balance ?? "0",
    formatCsvDate(order.deliveryDate),
    formatCsvDate(order.toBeDelivered),
    formatCsvDate(order.quotationDate),
    order.quotationTime ?? "",
    order.invoiceGenerated ? "Yes" : "No",
    order.invoiceId ?? "",
    formatCsvDate(order.invoiceDate),
    order.totalArea ?? "",
    order.notes ?? "",
    order.mobile ?? "",
  ]);

  downloadCsv(buildCsvFilename(opts.prefix, opts.fromDate, opts.toDate), headers, rows);
}

export function deliveryColumnLabelForRoles(roles: string[]): string {
  return resolveDeliveryColumnName({ prefix: "x", customerRoles: roles });
}

export { getToBeDeliveredFieldLabel };
